import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { MobileTruckLoadContext } from './MobileTruckLoadContext';
import { LoadingPortContext } from '../../../context/LoadingPortContext';
var redirectURL = require('../../redirectURL');

// One shared header + bottom nav sheet for the whole mobile flow, rendered once by
// MobileTruckLoadShell (not re-declared per screen like test.html did). Which
// button it shows (hamburger-menu vs. back-arrow) and where "back" goes is driven
// by the current route, via withRouter.
// Warehouse switching mid-scan (or while looking at one trip's summary) would
// invalidate whatever's currently on screen - it's scoped to a specific port -
// so switching is blocked on those two screens. Still shown (not hidden
// outright) so the operator can see which warehouse they're in without it
// looking like a live, tappable control there.
const SCREEN_META = {
    '/lm-mobile/landing': { mode: 'none', showPortBadge: true },
    '/lm-mobile/dashboard': { mode: 'menu', active: 'dashboard', showPortBadge: true },
    '/lm-mobile/input-plan': { mode: 'menu', active: 'form', showPortBadge: true },
    '/lm-mobile/scan': { mode: 'back', back: '/lm-mobile/input-plan', showPortBadge: true, portBadgeDisabled: true },
    '/lm-mobile/summary': { mode: 'back-dynamic', showPortBadge: true, portBadgeDisabled: true }
};

class MobileHeader extends Component {
    static contextType = MobileTruckLoadContext;

    state = { menuOpen: false, warehouseOpen: false, warehouseSwitching: false };

    componentWillUnmount() {
        clearTimeout(this.navTimer);
    }

    openMenu = () => { this.setState({ menuOpen: true }); };
    closeMenu = () => { this.setState({ menuOpen: false }); };

    openWarehouseSheet = () => { this.setState({ warehouseOpen: true }); };
    closeWarehouseSheet = () => { this.setState({ warehouseOpen: false }); };

    // Same lookup + endpoint Header.component.js's onChangeLoadingPlant uses on
    // desktop - lpNames only carries {loading_port_code, loading_port}, not
    // comp_code, so switching still needs this one call to resolve it before
    // portContext.setLoadingPort() can update the shared context/localStorage.
    selectWarehouse = (portContext, loading_port_code) => {
        if (loading_port_code === portContext.loading_port_code || this.state.warehouseSwitching) {
            this.closeWarehouseSheet();
            return;
        }
        // Keyed to the tapped warehouse, not just a plain boolean, so the
        // spinner shows on the one actually being switched to, not every item.
        this.setState({ warehouseSwitching: loading_port_code });
        redirectURL.post('/lmconsignees/getLoadingPlantDetails', { selectedPlant: loading_port_code })
            .then((response) => {
                var res = response.data;
                portContext.setLoadingPort(loading_port_code, res && res[0] ? res[0].comp_code : undefined);
                this.setState({ warehouseSwitching: false, warehouseOpen: false });
            })
            .catch((e) => {
                console.log('Error switching warehouse', e);
                this.setState({ warehouseSwitching: false, warehouseOpen: false });
            });
    };

    // Navigating (mounting the next screen, which for Input Plan also kicks off
    // its own data fetch) right as closeMenu() starts the sheet's own 0.25s
    // slide-down transition made that close look jerky/stutter instead of
    // smooth - the two were competing for the main thread at the exact moment
    // the transition needs consistent frame timing. Deferring navigation until
    // the transition actually finishes lets the sheet close cleanly first; the
    // next screen's own loader (Input Plan already has one) is what covers the
    // data fetch after that, instead of the menu appearing to hang open for it.
    goDashboard = () => {
        this.closeMenu();
        this.navTimer = setTimeout(() => this.props.history.push('/lm-mobile/dashboard'), 250);
    };

    goInputPlan = () => {
        this.closeMenu();
        this.navTimer = setTimeout(() => {
            // Opening Input Plan from the menu starts a fresh load - clear any
            // in-progress session instead of resuming it (resuming only applies to
            // the dedicated back-arrow from Scan, which returns to the same session).
            this.context.resetSessionState();
            this.props.history.push('/lm-mobile/input-plan');
        }, 250);
    };

    logout = () => {
        this.closeMenu();
        this.props.history.push('/logout');
    };

    goBack = () => {
        var meta = this.currentMeta();
        if (meta.mode === 'back') {
            this.props.history.push(meta.back);
        } else if (meta.mode === 'back-dynamic') {
            var opts = this.props.location.state;
            var base = (opts && opts.returnScreen) === 'dashboard' ? '/lm-mobile/dashboard' : '/lm-mobile/scan';
            // opts (router state) carries planId/tripNo already when it's present, but
            // it's exactly what a real refresh can lose - this component is a sibling
            // of the routed screen (see MobileTruckLoadShell), not wrapped by its own
            // Route, so its own `match.params` is the OUTER /lm-mobile route's (always
            // empty) - parsing the real location.pathname directly works regardless.
            var pathParts = this.props.location.pathname.split('/').filter(Boolean);
            var transactionId = (opts && opts.planId) || pathParts[2];
            var tripNo = (opts && opts.tripNo) || pathParts[3];
            var returnScreen = (base === '/lm-mobile/scan' && transactionId && tripNo)
                ? `/lm-mobile/scan/${transactionId}/${tripNo}`
                : base;
            this.props.history.push(returnScreen);
        }
    };

    currentMeta = () => {
        var pathname = this.props.location.pathname;
        // Exact match first (covers every screen with no params of its own, e.g.
        // /lm-mobile/dashboard), then a prefix match for Scan/Summary now that
        // they can carry /:transactionId/:tripNo after their own base path.
        var key = Object.keys(SCREEN_META).find((k) => pathname === k || pathname.indexOf(k + '/') === 0);
        return SCREEN_META[key] || { mode: 'none' };
    };

    render() {
        var meta = this.currentMeta();
        var isMenu = meta.mode === 'menu';
        var isBack = meta.mode === 'back' || meta.mode === 'back-dynamic';

        return (
            <LoadingPortContext.Consumer>
                {(portContext) => {
                    var lpNames = portContext.lpNames || [];
                    var current = lpNames.find((each) => each.loading_port_code === portContext.loading_port_code);
                    // loadingPointName (the friendly-name list lpNames comes from) isn't as
                    // reliably present as the port code itself - selected_loading_port_code
                    // (and comp_code) are already kept in sync by the desktop warehouse
                    // dropdown regardless. Fall back to the raw code so the badge still shows
                    // instead of disappearing whenever the name list is empty/stale.
                    var badgeText = current ? current.loading_port : portContext.loading_port_code;
                    // False either because there's genuinely only one warehouse to switch to,
                    // or because this screen (Scan/Summary - see meta.portBadgeDisabled)
                    // doesn't allow switching mid-trip even when there's more than one -
                    // either way it renders as the same plain, non-interactive label below.
                    var canSwitch = lpNames.length > 1 && !meta.portBadgeDisabled;
                    return (
                        <>
                            <div className="mtl-topbar">
                                {isMenu && (
                                    <button className="mtl-hamburger-btn" onClick={this.openMenu} aria-label="Menu">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                                    </button>
                                )}
                                {isBack && (
                                    <button className="mtl-hamburger-btn" onClick={this.goBack} aria-label="Back">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 18 14 12 20 6"></polyline>
                                            <polyline points="14 18 8 12 14 6"></polyline>
                                            <polyline points="8 18 2 12 8 6"></polyline>
                                        </svg>
                                    </button>
                                )}
                                <span className="mtl-brand"><img src={require('../../../assets/icons/optirun_v2.png')} alt="Optirun" /></span>
                                {meta.showPortBadge && badgeText && (
                                    canSwitch ? (
                                        <button type="button" className="mtl-sub" onClick={this.openWarehouseSheet}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px' }}>
                                                <path d="M3 21l0 -13l9 -4l9 4l0 13"></path>
                                                <path d="M13 13h4v8h-10v-6h6"></path>
                                                <path d="M3 21l18 0"></path>
                                            </svg>
                                            {badgeText}
                                        </button>
                                    ) : (
                                        // Either only one warehouse (nothing to switch to) or this
                                        // screen blocks switching outright (see canSwitch) - a
                                        // fixed label, not a control, either way. A <button> here
                                        // (even with no onClick) would still be keyboard-focusable,
                                        // read out as a button by screen readers, and show a
                                        // tap-highlight on mobile - a plain <span> is genuinely
                                        // non-interactive.
                                        <span className="mtl-sub">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px' }}>
                                                <path d="M3 21l0 -13l9 -4l9 4l0 13"></path>
                                                <path d="M13 13h4v8h-10v-6h6"></path>
                                                <path d="M3 21l18 0"></path>
                                            </svg>
                                            {badgeText}
                                        </span>
                                    )
                                )}
                            </div>

                            {isMenu && (
                                <>
                                    <div className={"mtl-nav-sheet-overlay" + (this.state.menuOpen ? " mtl-show" : "")} onClick={this.closeMenu}></div>
                                    <div className={"mtl-nav-sheet" + (this.state.menuOpen ? " mtl-show" : "")}>
                                        <div className="mtl-nav-sheet-handle"></div>
                                        <p className="mtl-nav-sheet-title">Navigate</p>
                                        <div className={"mtl-nav-sheet-item" + (meta.active === 'dashboard' ? " mtl-active" : "")} onClick={this.goDashboard}>
                                            <div className="mtl-nav-sheet-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9"></rect><rect x="14" y="3" width="7" height="5"></rect><rect x="14" y="12" width="7" height="9"></rect><rect x="3" y="16" width="7" height="5"></rect></svg>
                                            </div>
                                            <span className="mtl-nav-sheet-label">Dashboard</span>
                                        </div>
                                        <div className={"mtl-nav-sheet-item" + (meta.active === 'form' ? " mtl-active" : "")} onClick={this.goInputPlan}>
                                            <div className="mtl-nav-sheet-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="7" width="14" height="10" rx="1"></rect><path d="M15 10h4l3 3v4h-7z"></path><circle cx="6" cy="18.5" r="1.7"></circle><circle cx="17.5" cy="18.5" r="1.7"></circle></svg>
                                            </div>
                                            <span className="mtl-nav-sheet-label">Input Plan</span>
                                        </div>
                                        <div className="mtl-nav-sheet-divider"></div>
                                        {/* Scan screen's voice-over feedback language - stored on
                                            MobileTruckLoadContext (not local to this menu) so Scan,
                                            a sibling screen, reads the same value. */}
                                        <div className="mtl-nav-sheet-item" style={{ cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
                                            <div className="mtl-nav-sheet-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                                            </div>
                                            <span className="mtl-nav-sheet-label" style={{ flex: 1 }}>Voice Language</span>
                                            <select
                                                value={this.context.voiceLang}
                                                onChange={(e) => this.context.setVoiceLang(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', fontWeight: 600, background: '#fff' }}
                                            >
                                                <option value="en">English</option>
                                                <option value="hi">हिंदी (Hindi)</option>
                                            </select>
                                        </div>
                                        <div className="mtl-nav-sheet-item mtl-danger" onClick={this.logout}>
                                            <div className="mtl-nav-sheet-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                                            </div>
                                            <span className="mtl-nav-sheet-label">Logout</span>
                                        </div>
                                    </div>
                                </>
                            )}

                            {canSwitch && (
                                <>
                                    <div className={"mtl-nav-sheet-overlay" + (this.state.warehouseOpen ? " mtl-show" : "")} onClick={this.closeWarehouseSheet}></div>
                                    <div className={"mtl-nav-sheet" + (this.state.warehouseOpen ? " mtl-show" : "")}>
                                        <div className="mtl-nav-sheet-handle"></div>
                                        <p className="mtl-nav-sheet-title">Select Warehouse</p>
                                        {lpNames.map((each) => (
                                            <div
                                                key={each.loading_port_code}
                                                className={"mtl-nav-sheet-item" + (each.loading_port_code === portContext.loading_port_code ? " mtl-active" : "")}
                                                onClick={() => !this.state.warehouseSwitching && this.selectWarehouse(portContext, each.loading_port_code)}
                                            >
                                                <div className="mtl-nav-sheet-icon">
                                                    {this.state.warehouseSwitching === each.loading_port_code
                                                        ? <span className="mtl-spinner"></span>
                                                        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21l0 -13l9 -4l9 4l0 13"></path><path d="M13 13h4v8h-10v-6h6"></path><path d="M3 21l18 0"></path></svg>}
                                                </div>
                                                <span className="mtl-nav-sheet-label">{each.loading_port}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    );
                }}
            </LoadingPortContext.Consumer>
        );
    }
}

export default withRouter(MobileHeader);
